import React, { useEffect, useState } from 'react';
import {
    Box,
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    Grid,
    TextField,
    Typography,
    Autocomplete,
    FormGroup,
    Stack,
    IconButton,
    Backdrop,
    Chip
} from '@mui/material';
import TmFullButton from '../../common/buttons/TmFullButton';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { CompanyShortDto } from '../../../models/companies/companyShortDto';
import ErrorAlert from '../../common/ErrorAlert';
import { TextFieldFormControl } from '../../common/form/TextFieldFormControl';
import TmCross from '../../../icons/TmCross';
import { EditMedicFormDto, emptyEditMedicFormDto, mapToAddMedicDto, mapToEditMedicDto, mapToEditMedicFormDto } from '../../../models/medics/editMedicFormDto';
import { medicsService } from '../../../services/medicsService';
import { MedicDetailsDto } from '../../../models/medics/medicDetailsDto';
import { companiesService } from '../../../services/companiesService';
import ImageUploadWithPreview from '../../common/image/ImageUploadWithPreview';
import { ShowForPermission } from '../../common/permissions/ShowForPermission';
import { CategoryType } from '../../../models/enums/categoryType';
import { PermissionType } from '../../../models/enums/permissionType';
import DenyLabel from '../../common/labels/DenyLabel';
import AcceptLabel from '../../common/labels/AcceptLabel';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import Spinner from '../../common/Spinner';
import { CheckboxFormControl } from '../../common/form/CheckboxFormControl';
import { usersService } from '../../../services/usersService';
import { snilsValidationRules } from '../../../helpers/snilsUtils';

interface EditMedicModalProps {
    open: boolean;
    onClose: () => void;
    onRefresh?: () => void;
    isAdding: boolean;
    medic?: MedicDetailsDto;
    companyId?: number;
}

export const EditMedicModal: React.FC<EditMedicModalProps> = ({
    open,
    onClose,
    onRefresh,
    isAdding,
    medic,
    companyId
}) => {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [companies, setCompanies] = useState<CompanyShortDto[]>([]);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [openEsiaConfirmDialog, setOpenEsiaConfirmDialog] = useState(false);

    const methods = useForm<EditMedicFormDto>({
        defaultValues: medic ? mapToEditMedicFormDto(medic) : emptyEditMedicFormDto(companyId),
    });

    useEffect(() => {
        if (open) {
            const defaultValues = isAdding ? emptyEditMedicFormDto(companyId) : mapToEditMedicFormDto(medic!);
            methods.reset(defaultValues);
        }
    }, [open, isAdding, medic, methods, companyId]);

    useEffect(() => {
        companiesService
            .search({
                name: companyId ? undefined : '',
                companyIds: companyId ? [companyId] : undefined,
                isTelemed: false,
                page: 0,
                pageSize: 999,
            })
            .then(r => setCompanies(r.data.data.items))
            .catch(e => console.log(e));
    }, [companyId]);

    const onSubmit = (formData: EditMedicFormDto) => {
        setLoading(true);
        const serviceCall = isAdding
            ? medicsService.add(mapToAddMedicDto(formData))
            : medicsService.edit(mapToEditMedicDto(formData));

        serviceCall
            .then(r => {
                onClose();
                if (onRefresh) {
                    onRefresh();
                }
            })
            .catch(e => {
                const error = e.response?.data?.message ?? e.message;
                showAlert(error);
            })
            .finally(() => setLoading(false));
    };

    const onSubmitError = (errors: any) => {
        showAlert("Исправьте ошибки ввода");
        console.log('Form Errors:', errors);
    };

    const showAlert = (errorMessage: string = 'Произошла ошибка') => {
        setError(errorMessage);
    };

    const handleDismiss = () => {
        if (medic && medic.userId) {
            const isCurrentlyDismissed = medic.isDismissed;

            Promise.resolve()
                .then(() => {
                    setLoading(true);
                    setOpenConfirmDialog(false);
                    // Invert dismissal
                    const newDismissalStatus = !isCurrentlyDismissed;
                    return usersService.toggleDismissal(medic.userId, newDismissalStatus);
                })
                .then(() => {
                    methods.setValue("isDismissed", !medic.isDismissed);
                    if (onRefresh) {
                        onRefresh();
                    }
                })
                .catch(e => {
                    console.log(e);
                    setError(e.message);
                })
                .finally(() => setLoading(false));
        }
    }

    const handleUnlinkEsia = () => {
        if (medic) {
            Promise.resolve()
                .then(() => {
                    setLoading(true);
                    setOpenEsiaConfirmDialog(false);
                    return usersService.unlinkEsia(medic.userId);
                })
                .then(() => {
                    methods.setValue("esiaId", undefined);
                    if (onRefresh) {
                        onRefresh();
                    }
                })
                .catch(e => {
                    console.log(e);
                    setError(e.message);
                })
                .finally(() => setLoading(false));
        }
    }

    return (
        <Box>
            <FormProvider {...methods}>
                <Dialog
                    open={open}
                    onClose={onClose}
                    fullWidth
                    maxWidth={false}
                    PaperProps={{
                        sx: {
                            borderRadius: "16px",
                            width: '745px'
                        }
                    }}>
                    {error && (
                        <ErrorAlert
                            message={error}
                            onClose={() => setError('')}
                        />
                    )}
                    <DialogTitle sx={{ textAlign: 'center', marginTop: 2 }}>
                        <Box>
                            <Typography sx={{ fontSize: '32px', lineHeight: '1.2' }}>
                                {isAdding ? "Добавление" : "Редактирование"}
                            </Typography>
                            <Typography sx={{ fontSize: '32px', lineHeight: '1.2', marginTop: '0' }}>
                                медицинского работника
                            </Typography>
                        </Box>
                    </DialogTitle>
                    {!error && (
                        <IconButton
                            aria-label="close"
                            onClick={onClose}
                            sx={{
                                position: 'absolute',
                                right: 16,
                                top: 16,
                                color: (theme) => theme.palette.grey[500],
                            }}
                        >
                            <TmCross fill="none" stroke="#cccdd5" />
                        </IconButton>
                    )}
                    <DialogContent sx={{ padding: 0 }}>
                        <Box component="form" noValidate sx={{ px: '36px' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={4} sm={4}>
                                    <Typography sx={{ fontSize: '24px' }}>Личные данные</Typography>
                                </Grid>
                                <Grid item xs={8} sm={8}>
                                </Grid>
                                <Grid item xs={4} sm={4}>
                                    <ImageUploadWithPreview
                                        onFileChange={(file) => {
                                            methods.setValue('photoFile', file || undefined);
                                            if (!file) {
                                                methods.setValue('photoUrl', undefined);
                                            }
                                        }}
                                        file={methods.watch('photoFile')}
                                        url={methods.watch('photoUrl')}
                                        error={methods.formState.errors.photoFile?.message}
                                        textColor="#9588e8"
                                        buttonClassName={"list-item-outline"}
                                    />
                                </Grid>

                                <Grid item xs={8} sm={8}>
                                    <FormGroup>
                                        <Stack spacing={2} direction="column">
                                            <Box display="flex">
                                                <TextFieldFormControl
                                                    name="lastName"
                                                    label="Фамилия"
                                                    required
                                                    maxLength={50}
                                                />
                                            </Box>
                                            <Box display="flex">
                                                <TextFieldFormControl
                                                    name="firstName"
                                                    label="Имя"
                                                    required
                                                    maxLength={50}
                                                    style={{ marginRight: 10 }}
                                                />
                                                <TextFieldFormControl
                                                    name="middleName"
                                                    label="Отчество"
                                                    required
                                                    maxLength={50}
                                                />
                                            </Box>
                                            <Box display="flex">
                                                <TextFieldFormControl
                                                    name="phoneNumber"
                                                    label="Телефон"
                                                    required
                                                    maxLength={20}
                                                    style={{ marginRight: 10 }}
                                                />
                                                <TextFieldFormControl
                                                    name="email"
                                                    label="Email"
                                                    required
                                                    type="email"
                                                    maxLength={128}
                                                    validationRules={{
                                                        pattern: {
                                                            value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                                            message: 'Невалидный email',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                            <Box display="flex">
                                                <TextFieldFormControl
                                                    name="snils"
                                                    label="СНИЛС"
                                                    mask="999-999-999 99"
                                                    placeholder="123-456-789 00"
                                                    validationRules={snilsValidationRules}
                                                />

                                            </Box>
                                        </Stack>
                                    </FormGroup>
                                </Grid>

                                <Grid item xs={12} sm={12} mt={2}>
                                    <Typography sx={{ fontSize: '24px' }}>Клиенты</Typography>
                                </Grid>

                                <Grid item xs={12} sm={12} mb={2}>
                                    <CheckboxFormControl
                                        name="isTelemed"
                                        label="Telemed"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} mb={2}>
                                    <FormGroup>
                                        <Controller
                                            name="companyIds"
                                            control={methods.control}
                                            rules={{
                                                validate: (v) =>
                                                    methods.getValues('isTelemed') ||
                                                    (v && v.length > 0) ||
                                                    'Выберите хотя бы одного клиента'
                                            }}
                                            render={({ field: { onChange, value } }) => (
                                                <Autocomplete
                                                    multiple
                                                    size="small"
                                                    options={companyId ? [] : companies}
                                                    getOptionLabel={(item) => item?.name ?? ''}
                                                    value={companies.filter(c => value?.includes(c.id) ?? false)}
                                                    onChange={(_, newValue) => {
                                                        if (!companyId) {
                                                            onChange(newValue.map(c => c.id));
                                                        }
                                                    }}
                                                    disabled={!!companyId}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Клиенты"
                                                            placeholder="Введите от 3х букв"
                                                            error={Boolean(methods.formState.errors.companyIds)}
                                                            helperText={methods.formState.errors.companyIds?.message as string}
                                                            required={!methods.watch('isTelemed')}
                                                        />
                                                    )}
                                                    renderTags={(val, getTagProps) =>
                                                        val.map((option, index) => (
                                                            <Chip label={option.name} {...getTagProps({ index })} key={option.id} />
                                                        ))
                                                    }
                                                />
                                            )}
                                        />
                                    </FormGroup>
                                </Grid>
                            </Grid>

                            <Grid container spacing={2}>

                                {!isAdding &&
                                    <>
                                        <Grid item xs={12} sm={12}>
                                            <Typography sx={{ fontSize: '24px' }}>Статус</Typography>
                                        </Grid><Grid item xs={6} sm={6}>

                                            <Typography className="rtb">
                                                {medic?.isDismissed ? <DenyLabel>Уволен</DenyLabel> : <AcceptLabel>Работает</AcceptLabel>}
                                            </Typography>
                                        </Grid><Grid item xs={6} sm={6}>
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <ShowForPermission catalog={CategoryType.Medics} permission={PermissionType.Delete}>
                                                    <TmFullButton
                                                        onClick={() => setOpenConfirmDialog(true)}
                                                        sx={{ fontSize: '14px', mt: -2 }}
                                                    >
                                                        {medic?.isDismissed ? "Восстановить" : "Уволить"}
                                                    </TmFullButton>
                                                </ShowForPermission>
                                            </Box>
                                        </Grid>
                                    </>
                                }

                                {!isAdding && medic?.esiaId &&
                                    <>
                                        <Grid item xs={12} sm={12}>
                                            <Typography sx={{ fontSize: '24px' }}>Госуслуги</Typography>
                                        </Grid><Grid item xs={6} sm={6}>

                                            <Typography className="rtb">
                                                <AcceptLabel>Привязан</AcceptLabel>
                                            </Typography>
                                        </Grid><Grid item xs={6} sm={6}>
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <ShowForPermission catalog={CategoryType.Medics} permission={PermissionType.Edit}>
                                                    <TmFullButton
                                                        onClick={() => setOpenEsiaConfirmDialog(true)}
                                                        sx={{ fontSize: '14px', mt: -2 }}
                                                    >
                                                        Отвязать
                                                    </TmFullButton>
                                                </ShowForPermission>
                                            </Box>
                                        </Grid>
                                    </>
                                }

                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions style={{ justifyContent: 'center' }} sx={{ pb: '36px', pt: 3 }}>
                        <TmFullButton onClick={methods.handleSubmit(onSubmit, onSubmitError)} width='45%'>
                            Сохранить
                        </TmFullButton>
                    </DialogActions>
                </Dialog>
            </FormProvider>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
                open={loading}
            >
                <Spinner />
            </Backdrop>
            <ConfirmDialog
                open={openConfirmDialog}
                title={medic?.isDismissed ? "Восстановление медицинского работника" : "Увольнение медицинского работника"}
                content={
                    <>
                        {medic?.isDismissed
                            ? "Вы уверены, что хотите восстановить сотрудника"
                            : "Вы уверены, что хотите уволить сотрудника"
                        }
                        <Typography variant="body1" align="center" fontWeight="bold">
                            {`${medic?.lastName} ${medic?.firstName} ${medic?.middleName}?`}
                        </Typography>
                    </>
                }
                onClose={() => setOpenConfirmDialog(false)}
                onConfirm={handleDismiss}
            />
            <ConfirmDialog
                open={openEsiaConfirmDialog}
                title="Отвязка от Госуслуг"
                content={
                    <>
                        Вы уверены, что хотите отвязать медицинского работника от Госуслуг
                        <Typography variant="body1" align="center" fontWeight="bold">
                            {`${medic?.lastName} ${medic?.firstName} ${medic?.middleName}?`}
                        </Typography>
                    </>
                }
                onClose={() => setOpenEsiaConfirmDialog(false)}
                onConfirm={handleUnlinkEsia}
            />
        </Box >
    );
};
